import { cn } from '@/lib/utils';
import { formatCrownDollars, offerBonusCents, type OfferOption } from '@/lib/monetization';
import { SpriteSheetPlayer } from '@/components/ui';
import { scopeLabel } from './scope-label';
import { crownBadgeData, crownBadgeSheet, crownFrames } from '@/lib/crown-frames';

export interface OfferTierProps {
  option: OfferOption;
  /** Flags the highest-value tier with a "Best Value" tab. */
  best?: boolean;
  /** Selected styling (purple ring). Ignored when static. */
  selected?: boolean;
  /**
   * Makes the tier an interactive radio-style button. Omit to render a static
   * read-only row (used inside the offer detail sheet preview).
   */
  onSelect?: () => void;
}

/**
 * One deposit-offer tier ("Choose Your Boost" pack), framed as a concrete
 * reward:
 *
 *     ┌ Best Value ┐
 *     │  $100 + Get 35 ◈ Dollars     │
 *     │  *Crown Dollars usable on …  │
 *     └──────────────────────────────┘
 *
 * Cash-Back tokens are $1-equivalent, so the bonus is shown as a real Crown
 * Dollars amount (computed from the tier's percent) rather than a bare "%".
 * The selected tier carries a purple border; the best-value tier gets a tab.
 * Interactive in the detail sheet (`onSelect`); static where omitted.
 */
export function OfferTier({ option, best, selected = false, onSelect }: OfferTierProps) {
  const interactive = onSelect != null;
  const bonusCents = offerBonusCents(option);
  const Tag = interactive ? 'button' : 'div';
  const deposit = `$${(option.depositCents / 100).toLocaleString()}`;

  return (
    <div className="relative">
      {best && (
        <span className="absolute bottom-full left-4 z-[1] rounded-t-[10px] bg-[linear-gradient(127deg,var(--color-brand-grad-3)_0%,var(--color-brand-grad-2)_50%,var(--color-brand-grad-1)_100%)] px-3 pb-1.5 pt-0.5 text-[11px] font-medium leading-none text-white">
          Best Value
        </span>
      )}

      <Tag
        {...(interactive ? { type: 'button' as const, onClick: onSelect, 'aria-pressed': selected } : {})}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-1 rounded-card border-[1.5px] bg-surface px-4 py-3.5 text-center transition-colors',
          selected ? 'border-brand-grad-3' : 'border-stroke-soft',
          interactive && !selected && 'hover:border-purple/40',
          interactive &&
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 focus-visible:ring-offset-2',
        )}
      >
        <span className="flex items-center justify-center gap-1 leading-none">
          <span className="text-[20px] font-bold text-ink">{deposit}</span>
          <span className="text-[18px] font-medium text-ink">+</span>
          <span className="text-[18px] font-medium text-purple">Get</span>
          <span className="text-[20px] font-bold text-purple">{formatCrownDollars(bonusCents)}</span>
          <SpriteSheetPlayer
            spriteSheetSrc={crownBadgeSheet}
            sheetWidth={crownBadgeData.meta.size.w}
            sheetHeight={crownBadgeData.meta.size.h}
            frames={crownFrames}
            frameDurationMs={30}
            playback="pingpong"
            loopDelayMs={4000}
            width={28}
          />
          <span className="text-[20px] font-bold text-purple">Dollars</span>
        </span>
        <span className="text-[12px] leading-tight text-muted">*Crown Dollars usable on {scopeLabel(option.scope)}</span>
      </Tag>
    </div>
  );
}
